// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
export type PayrollDisplayUnit = "hours" | "days";

export type PayrollCrossCheck = {
  state?: "green" | "yellow" | "red" | string;
  timesheetValue?: number | null;
  payslipValue?: number | null;
  difference?: number | null;
};

export type PayrollPeriodDisplayRow = {
  hasResult?: boolean;
  hoursPerDay?: number | null;
  crossChecks?: Record<string, PayrollCrossCheck | undefined>;
};

const resolvedHoursPerDay = (hoursPerDay: number | null | undefined) =>
  typeof hoursPerDay === "number" && hoursPerDay > 0 ? hoursPerDay : 8;

const comparisonMatches = (
  check: PayrollCrossCheck | undefined,
  left: number | null | undefined,
) =>
  check?.state === "green" &&
  typeof left === "number" &&
  typeof check.payslipValue === "number" &&
  Math.abs(left - check.payslipValue) < 0.005;

export const toDisplayValue = (
  valueHours: number | null | undefined,
  unit: PayrollDisplayUnit,
  hoursPerDay?: number | null,
) => {
  if (typeof valueHours !== "number") return null;
  return unit === "days"
    ? valueHours / resolvedHoursPerDay(hoursPerDay)
    : valueHours;
};

export const formatDisplayValue = (
  value: number | null | undefined,
  unit: PayrollDisplayUnit,
) =>
  typeof value === "number"
    ? `${value.toFixed(2)}${unit === "days" ? "d" : "h"}`
    : "-";

export const formatDuration = (
  valueHours: number | null | undefined,
  unit: PayrollDisplayUnit,
  hoursPerDay?: number | null,
) => formatDisplayValue(toDisplayValue(valueHours, unit, hoursPerDay), unit);

export const formatComparisonPair = (
  check: PayrollCrossCheck | undefined,
  fallbackLeftHours: number | null | undefined,
  unit: PayrollDisplayUnit,
  hoursPerDay?: number | null,
) => {
  const leftHours =
    typeof check?.timesheetValue === "number"
      ? check.timesheetValue
      : fallbackLeftHours;
  if (comparisonMatches(check, leftHours)) {
    return formatDuration(leftHours, unit, hoursPerDay);
  }
  return `${formatDuration(leftHours, unit, hoursPerDay)} | ${formatDuration(check?.payslipValue, unit, hoursPerDay)}`;
};

export const mismatchTooltip = (
  check: PayrollCrossCheck | undefined,
  unit: PayrollDisplayUnit,
  hoursPerDay?: number | null,
) => {
  if (!check || check.state === "green") return undefined;
  if (typeof check.difference === "number") {
    return `Mismatch: ${formatDuration(Math.abs(check.difference), unit, hoursPerDay)}`;
  }
  if (check.timesheetValue === null && check.payslipValue === null)
    return "Mismatch: source values missing";
  if (check.timesheetValue === null) return "Mismatch: timesheet value missing";
  if (check.payslipValue === null) return "Mismatch: payslip value missing";
  return "Mismatch: value unavailable";
};

export const periodDisplayValue = <TRow extends PayrollPeriodDisplayRow>(
  rows: TRow[],
  selector: (row: TRow) => number | null | undefined,
  unit: PayrollDisplayUnit,
) => {
  let hasValue = false;
  const total = rows.reduce((sum, row) => {
    if (row.hasResult === false) return sum;
    const value = selector(row);
    if (typeof value !== "number") return sum;
    hasValue = true;
    return sum + (toDisplayValue(value, unit, row.hoursPerDay) ?? 0);
  }, 0);
  return hasValue ? total : null;
};

export const aggregatePeriodCrossCheck = <TRow extends PayrollPeriodDisplayRow>(
  rows: TRow[],
  key: string,
  fallbackLeft: (row: TRow) => number | null | undefined,
  unit: PayrollDisplayUnit,
): PayrollCrossCheck | undefined => {
  const completedRows = rows.filter((row) => row.hasResult !== false);
  const checks = completedRows
    .map((row) => ({ row, check: row.crossChecks?.[key] }))
    .filter((entry): entry is { row: TRow; check: PayrollCrossCheck } =>
      Boolean(entry.check),
    );

  if (!checks.length) {
    const fallback = periodDisplayValue(completedRows, fallbackLeft, unit);
    return fallback === null
      ? undefined
      : { timesheetValue: fallback, payslipValue: null, difference: null };
  }

  const states = checks.map(({ check }) => check.state);
  const state = states.includes("red")
    ? "red"
    : states.includes("yellow")
      ? "yellow"
      : "green";
  const leftValues = checks.map(({ row, check }) => {
    const value =
      typeof check.timesheetValue === "number"
        ? check.timesheetValue
        : fallbackLeft(row);
    return toDisplayValue(value, unit, row.hoursPerDay);
  });
  const hasMissingPayslip = checks.some(
    ({ check }) =>
      check.state !== "green" && typeof check.payslipValue !== "number",
  );
  const rightValues = checks.map(({ row, check }) => {
    if (typeof check.payslipValue === "number")
      return toDisplayValue(check.payslipValue, unit, row.hoursPerDay);
    return check.state === "green" ? 0 : null;
  });
  const mismatches = checks
    .filter(
      ({ check }) =>
        check.state !== "green" && typeof check.difference === "number",
    )
    .map(({ row, check }) =>
      Math.abs(toDisplayValue(check.difference, unit, row.hoursPerDay) ?? 0),
    );
  const sum = (values: Array<number | null>) =>
    values.reduce<number>((total, value) => total + (value ?? 0), 0);

  return {
    state,
    timesheetValue: leftValues.some((value) => value !== null)
      ? sum(leftValues)
      : null,
    payslipValue: hasMissingPayslip
      ? null
      : rightValues.some((value) => value !== null)
        ? sum(rightValues)
        : null,
    difference: mismatches.length
      ? sum(mismatches)
      : state === "green"
        ? 0
        : null,
  };
};

export const formatDisplayComparisonPair = (
  check: PayrollCrossCheck | undefined,
  fallbackLeft: number | null | undefined,
  unit: PayrollDisplayUnit,
) => {
  const left =
    typeof check?.timesheetValue === "number"
      ? check.timesheetValue
      : fallbackLeft;
  if (comparisonMatches(check, left)) {
    return formatDisplayValue(left, unit);
  }
  return `${formatDisplayValue(left, unit)} | ${formatDisplayValue(check?.payslipValue, unit)}`;
};

export const displayMismatchTooltip = (
  check: PayrollCrossCheck | undefined,
  unit: PayrollDisplayUnit,
) => {
  if (!check || check.state === "green") return undefined;
  if (typeof check.difference === "number")
    return `Mismatch: ${formatDisplayValue(Math.abs(check.difference), unit)}`;
  if (check.timesheetValue === null && check.payslipValue === null)
    return "Mismatch: source values missing";
  if (check.timesheetValue === null) return "Mismatch: timesheet value missing";
  if (check.payslipValue === null) return "Mismatch: payslip value missing";
  return "Mismatch: value unavailable";
};
